import { useState } from 'react';
import { toast } from 'sonner';
import { Pencil, Plus, Save, Trash2, X } from 'lucide-react';
import {
  useAdminDayparts,
  useAdminProducts,
  useDeleteDaypart,
  useDeleteProduct,
  useDeleteRateEntry,
  useSaveDaypart,
  useSaveProduct,
  useSaveRateEntry,
} from '@/api/ads/hooks';
import type { AdProductInput, DaypartInput } from '@/api/ads/hooks';
import { extractErrorMessage } from '@/api/client';
import { ResourceGate } from '@/features/ResourceGate';
import { useAccess } from '@/features/AccessProvider';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardHeader, CardBody } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Table, Th, Td } from '@/components/ui/table';
import { Spinner } from '@/components/ui/spinner';
import { AD_BILLING_UNITS, AD_SURFACES } from '@/lib/ads-catalog';
import { npr, titleCase } from '@/lib/format';
import type { AdProduct, Daypart, RateEntry } from '@/api/ads/types';

const emptyProduct = (): AdProductInput => ({
  code: '',
  name: '',
  description: '',
  surface: 'home',
  billing_unit: 'per_month',
  default_total_slots: 1,
  requires_daypart: true,
  is_active: true,
  sort_order: 0,
});

const emptyDaypart = (): DaypartInput => ({
  code: '',
  label: '',
  start_time: '06:00:00',
  end_time: '11:00:00',
  sort_order: 0,
  is_active: true,
});

export function RateCardPage() {
  const products = useAdminProducts();
  const dayparts = useAdminDayparts();
  const { can, isSuperuser } = useAccess();
  const canManage = can('ads.rate-card', 'manage');
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [showDayparts, setShowDayparts] = useState(false);

  return (
    <ResourceGate slug="ads.rate-card">
      <PageHeader
        title="Rate Card"
        description="Editable pricing that drives advertiser quotes and inventory."
        action={
          isSuperuser ? (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setShowDayparts((v) => !v)}>
                {showDayparts ? 'Hide dayparts' : 'Manage dayparts'}
              </Button>
              <Button size="sm" onClick={() => setShowNewProduct((v) => !v)}>
                <Plus className="h-4 w-4" /> New product
              </Button>
            </div>
          ) : undefined
        }
      />

      {isSuperuser && showDayparts && (
        <DaypartsPanel dayparts={dayparts.data ?? []} loading={dayparts.isLoading} />
      )}

      {isSuperuser && showNewProduct && (
        <NewProductPanel onDone={() => setShowNewProduct(false)} />
      )}

      {products.isLoading ? (
        <Spinner label="Loading products…" />
      ) : (
        <div className="space-y-5">
          {(products.data ?? []).map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              dayparts={(dayparts.data ?? []).filter((d) => d.is_active)}
              canManage={canManage}
              canEditCatalog={isSuperuser}
            />
          ))}
          {!products.data?.length && (
            <Card className="p-8 text-center text-sm text-muted-foreground">
              No ad products yet.
              {isSuperuser ? ' Use “New product” to add the first placement type.' : ' Ask a superuser to seed the catalogue.'}
            </Card>
          )}
        </div>
      )}
    </ResourceGate>
  );
}

function DaypartsPanel({ dayparts, loading }: { dayparts: Daypart[]; loading: boolean }) {
  const save = useSaveDaypart();
  const del = useDeleteDaypart();
  const [adding, setAdding] = useState(false);

  return (
    <Card className="mb-5">
      <CardHeader title="Dayparts" description="Time-of-day slices used for per-daypart pricing." />
      <CardBody className="p-0">
        {loading ? (
          <div className="p-6">
            <Spinner label="Loading dayparts…" />
          </div>
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Code</Th>
                  <Th>Label</Th>
                  <Th>Start</Th>
                  <Th>End</Th>
                  <Th>Order</Th>
                  <Th>Active</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {dayparts.map((d) => (
                  <DaypartRow key={d.id} daypart={d} onSave={save} onDelete={del} />
                ))}
                {adding && (
                  <NewDaypartRow onDone={() => setAdding(false)} onSave={save} />
                )}
              </tbody>
            </Table>
            <div className="border-t p-3">
              <Button size="sm" variant="outline" onClick={() => setAdding((v) => !v)}>
                <Plus className="h-4 w-4" /> Add daypart
              </Button>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function DaypartRow({
  daypart,
  onSave,
  onDelete,
}: {
  daypart: Daypart;
  onSave: ReturnType<typeof useSaveDaypart>;
  onDelete: ReturnType<typeof useDeleteDaypart>;
}) {
  const [label, setLabel] = useState(daypart.label);
  const [start, setStart] = useState(daypart.start_time.slice(0, 5));
  const [end, setEnd] = useState(daypart.end_time.slice(0, 5));
  const [order, setOrder] = useState(String(daypart.sort_order));
  const [active, setActive] = useState(daypart.is_active);

  const save = async () => {
    try {
      await onSave.mutateAsync({
        id: daypart.id,
        label,
        start_time: `${start}:00`,
        end_time: `${end}:00`,
        sort_order: Number(order),
        is_active: active,
      });
      toast.success('Daypart updated.');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete daypart “${daypart.label}”?`)) return;
    try {
      await onDelete.mutateAsync(daypart.id);
      toast.success('Daypart deleted.');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <tr>
      <Td className="font-mono text-xs text-muted-foreground">{daypart.code}</Td>
      <Td><Input className="h-8" value={label} onChange={(e) => setLabel(e.target.value)} /></Td>
      <Td><Input className="h-8 w-24" type="time" value={start} onChange={(e) => setStart(e.target.value)} /></Td>
      <Td><Input className="h-8 w-24" type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></Td>
      <Td><Input className="h-8 w-16" type="number" value={order} onChange={(e) => setOrder(e.target.value)} /></Td>
      <Td><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /></Td>
      <Td className="text-right">
        <div className="flex justify-end gap-1">
          <Button size="icon" variant="ghost" onClick={save} loading={onSave.isPending}><Save className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" onClick={remove}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </Td>
    </tr>
  );
}

function NewDaypartRow({
  onDone,
  onSave,
}: {
  onDone: () => void;
  onSave: ReturnType<typeof useSaveDaypart>;
}) {
  const [form, setForm] = useState(emptyDaypart);
  const set = <K extends keyof DaypartInput>(key: K, value: DaypartInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const create = async () => {
    try {
      await onSave.mutateAsync({
        ...form,
        start_time: `${form.start_time.slice(0, 5)}:00`,
        end_time: `${form.end_time.slice(0, 5)}:00`,
      });
      toast.success('Daypart created.');
      onDone();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <tr className="bg-muted/40">
      <Td><Input className="h-8 font-mono text-xs" placeholder="d_6_11" value={form.code} onChange={(e) => set('code', e.target.value)} /></Td>
      <Td><Input className="h-8" placeholder="6AM - 11AM" value={form.label} onChange={(e) => set('label', e.target.value)} /></Td>
      <Td><Input className="h-8 w-24" type="time" value={form.start_time.slice(0, 5)} onChange={(e) => set('start_time', e.target.value)} /></Td>
      <Td><Input className="h-8 w-24" type="time" value={form.end_time.slice(0, 5)} onChange={(e) => set('end_time', e.target.value)} /></Td>
      <Td><Input className="h-8 w-16" type="number" value={form.sort_order} onChange={(e) => set('sort_order', Number(e.target.value))} /></Td>
      <Td><input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} /></Td>
      <Td className="text-right">
        <div className="flex justify-end gap-1">
          <Button size="sm" onClick={create} loading={onSave.isPending}>Save</Button>
          <Button size="icon" variant="ghost" onClick={onDone}><X className="h-4 w-4" /></Button>
        </div>
      </Td>
    </tr>
  );
}

function NewProductPanel({ onDone }: { onDone: () => void }) {
  const save = useSaveProduct();
  const [form, setForm] = useState(emptyProduct);
  const set = <K extends keyof AdProductInput>(key: K, value: AdProductInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const create = async () => {
    try {
      await save.mutateAsync(form);
      toast.success('Product created.');
      onDone();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Card className="mb-5">
      <CardHeader title="New ad product" description="A sellable placement type on the rate card." />
      <CardBody>
        <ProductFields form={form} set={set} showCode />
        <div className="mt-4 flex gap-2">
          <Button onClick={create} loading={save.isPending}>Create product</Button>
          <Button variant="outline" onClick={onDone}>Cancel</Button>
        </div>
      </CardBody>
    </Card>
  );
}

function ProductFields({
  form,
  set,
  showCode,
}: {
  form: AdProductInput;
  set: <K extends keyof AdProductInput>(key: K, value: AdProductInput[K]) => void;
  showCode?: boolean;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {showCode && (
        <div>
          <Label>Code</Label>
          <Input value={form.code} placeholder="home_banner" onChange={(e) => set('code', e.target.value)} />
          <p className="mt-1 text-xs text-muted-foreground">Lowercase slug, e.g. checkout_offer</p>
        </div>
      )}
      <div>
        <Label>Name</Label>
        <Input value={form.name} onChange={(e) => set('name', e.target.value)} />
      </div>
      <div className="sm:col-span-2">
        <Label>Description</Label>
        <Textarea value={form.description} onChange={(e) => set('description', e.target.value)} rows={2} />
      </div>
      <div>
        <Label>Surface</Label>
        <Select value={form.surface} onChange={(e) => set('surface', e.target.value)}>
          {AD_SURFACES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </Select>
      </div>
      <div>
        <Label>Billing unit</Label>
        <Select value={form.billing_unit} onChange={(e) => set('billing_unit', e.target.value)}>
          {AD_BILLING_UNITS.map((u) => (
            <option key={u.value} value={u.value}>{u.label}</option>
          ))}
        </Select>
      </div>
      <div>
        <Label>Default slots</Label>
        <Input type="number" min={1} value={form.default_total_slots} onChange={(e) => set('default_total_slots', Number(e.target.value))} />
      </div>
      <div>
        <Label>Sort order</Label>
        <Input type="number" value={form.sort_order} onChange={(e) => set('sort_order', Number(e.target.value))} />
      </div>
      <div className="flex items-center gap-6 sm:col-span-2">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.requires_daypart} onChange={(e) => set('requires_daypart', e.target.checked)} />
          Requires daypart
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
          Active
        </label>
      </div>
    </div>
  );
}

function ProductCard({
  product,
  dayparts,
  canManage,
  canEditCatalog,
}: {
  product: AdProduct;
  dayparts: { id: number; label: string }[];
  canManage: boolean;
  canEditCatalog: boolean;
}) {
  const saveRate = useSaveRateEntry();
  const delRate = useDeleteRateEntry();
  const saveProduct = useSaveProduct();
  const delProduct = useDeleteProduct();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<AdProductInput>({
    code: product.code,
    name: product.name,
    description: product.description,
    surface: product.surface,
    billing_unit: product.billing_unit,
    default_total_slots: product.default_total_slots,
    requires_daypart: product.requires_daypart,
    is_active: product.is_active,
    sort_order: product.sort_order,
  });
  const set = <K extends keyof AdProductInput>(key: K, value: AdProductInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const saveProductMeta = async () => {
    try {
      await saveProduct.mutateAsync({ id: product.id, ...form });
      toast.success('Product updated.');
      setEditing(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const removeProduct = async () => {
    if (!window.confirm(`Delete product “${product.name}”?`)) return;
    try {
      await delProduct.mutateAsync(product.id);
      toast.success('Product deleted.');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Card>
      <CardHeader
        title={product.name}
        description={`${product.surface_display} · ${product.billing_unit_display} · capacity ${product.default_total_slots}`}
        action={
          <div className="flex gap-1">
            {canEditCatalog && (
              <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)}>
                <Pencil className="h-4 w-4" /> {editing ? 'Close' : 'Edit'}
              </Button>
            )}
            {canManage && (
              <Button size="sm" variant="outline" onClick={() => setAdding((v) => !v)}>
                <Plus className="h-4 w-4" /> Add rate
              </Button>
            )}
          </div>
        }
      />
      {editing && canEditCatalog && (
        <CardBody className="border-b">
          <p className="mb-3 font-mono text-xs text-muted-foreground">code: {product.code}</p>
          <ProductFields form={form} set={set} />
          <div className="mt-4 flex gap-2">
            <Button size="sm" onClick={saveProductMeta} loading={saveProduct.isPending}>Save product</Button>
            <Button size="sm" variant="danger" onClick={removeProduct}>Delete</Button>
          </div>
        </CardBody>
      )}
      <CardBody className="p-0">
        <Table>
          <thead>
            <tr>
              <Th>Daypart</Th>
              <Th>Unit</Th>
              <Th>Price</Th>
              <Th>Slots</Th>
              <Th>Implied CPM</Th>
              <Th>Active</Th>
              {canManage && <Th />}
            </tr>
          </thead>
          <tbody>
            {product.rates.map((r) => (
              <RateRow key={r.id} rate={r} canManage={canManage} onSave={saveRate} onDelete={delRate} />
            ))}
            {adding && canManage && (
              <NewRateRow
                productId={product.id}
                unit={product.billing_unit}
                dayparts={dayparts}
                onDone={() => setAdding(false)}
                onSave={saveRate}
              />
            )}
          </tbody>
        </Table>
      </CardBody>
    </Card>
  );
}

function RateRow({
  rate,
  canManage,
  onSave,
  onDelete,
}: {
  rate: RateEntry;
  canManage: boolean;
  onSave: ReturnType<typeof useSaveRateEntry>;
  onDelete: ReturnType<typeof useDeleteRateEntry>;
}) {
  const [price, setPrice] = useState(rate.price);
  const [slots, setSlots] = useState(String(rate.total_slots));
  const [active, setActive] = useState(rate.is_active);

  const save = async () => {
    try {
      await onSave.mutateAsync({ id: rate.id, price, total_slots: Number(slots), is_active: active });
      toast.success('Rate updated.');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <tr>
      <Td>{rate.daypart_label ?? 'All day'}</Td>
      <Td className="text-muted-foreground">{titleCase(rate.unit)}</Td>
      <Td>
        {canManage ? (
          <Input className="h-8 w-28" value={price} onChange={(e) => setPrice(e.target.value)} />
        ) : (
          npr(rate.price)
        )}
      </Td>
      <Td>
        {canManage ? (
          <Input className="h-8 w-20" type="number" value={slots} onChange={(e) => setSlots(e.target.value)} />
        ) : (
          rate.total_slots
        )}
      </Td>
      <Td className="text-muted-foreground">{rate.implied_cpm ? npr(rate.implied_cpm) : '—'}</Td>
      <Td>
        <input type="checkbox" checked={active} disabled={!canManage} onChange={(e) => setActive(e.target.checked)} />
      </Td>
      {canManage && (
        <Td className="text-right">
          <div className="flex justify-end gap-1">
            <Button size="icon" variant="ghost" onClick={save} loading={onSave.isPending}>
              <Save className="h-4 w-4" />
            </Button>
            <Button size="icon" variant="ghost" onClick={() => onDelete.mutate(rate.id)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </Td>
      )}
    </tr>
  );
}

function NewRateRow({
  productId,
  unit,
  dayparts,
  onDone,
  onSave,
}: {
  productId: number;
  unit: string;
  dayparts: { id: number; label: string }[];
  onDone: () => void;
  onSave: ReturnType<typeof useSaveRateEntry>;
}) {
  const [daypart, setDaypart] = useState('');
  const [price, setPrice] = useState('');
  const [slots, setSlots] = useState('1');

  const create = async () => {
    try {
      await onSave.mutateAsync({
        product: productId,
        daypart: daypart ? Number(daypart) : null,
        unit,
        price,
        total_slots: Number(slots),
        is_active: true,
        effective_from: new Date().toISOString().slice(0, 10),
      });
      toast.success('Rate added.');
      onDone();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <tr className="bg-muted/40">
      <Td>
        <Select className="h-8 w-36" value={daypart} onChange={(e) => setDaypart(e.target.value)}>
          <option value="">All day</option>
          {dayparts.map((d) => (
            <option key={d.id} value={d.id}>
              {d.label}
            </option>
          ))}
        </Select>
      </Td>
      <Td className="text-muted-foreground">{titleCase(unit)}</Td>
      <Td>
        <Input className="h-8 w-28" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0" />
      </Td>
      <Td>
        <Input className="h-8 w-20" type="number" value={slots} onChange={(e) => setSlots(e.target.value)} />
      </Td>
      <Td>—</Td>
      <Td>—</Td>
      <Td className="text-right">
        <Button size="sm" onClick={create} loading={onSave.isPending}>
          Save
        </Button>
      </Td>
    </tr>
  );
}
